import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { Eye, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { usePreviewMode } from "../../common/contexts/PreviewModeContext";
import Button from "../../common/components/Button";
import PageHeader from "../../common/components/PageHeader";
import { toast } from "../../common/components/Toast";
import { SPACING } from "../../common/constants/spacing";
import { useProject } from "../../common/contexts/ProjectContext";
import { resolveDateRange } from "../../common/contexts/SettingsContext";
import {
  deleteProject,
  getProjectInfo,
  updateProjectInfo,
} from "../../projects/api/project";
import ProjectIconUpload from "../../projects/components/ProjectIconUpload";
import {
  ANALYTICS_CARDS,
  ASSET_ANALYTICS_CARDS,
  DASHBOARD_CARDS,
  buildDefaultConfig,
} from "../../common/constants/cardRegistry";
import {
  COLORWAY_PRESETS,
  COLOR_SLOT_LABELS,
  DEFAULT_COLORWAY,
} from "../../common/constants/colorwayPresets";
import SortableCardList from "../components/SortableCardList";
import {
  activateDisplayTemplate,
  createDisplayTemplate,
  deleteDisplayTemplate,
  getDisplayTemplates,
  pushDisplayTemplate,
  resetMyPreferences,
  saveMyPreferences,
  shareDisplayTemplate,
  updateDisplayTemplate,
} from "../api/displayTemplates";
import { getMyProjects } from "../../projects/api/project";

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index) {
  return {
    id: `settings-tab-${index}`,
    "aria-controls": `settings-tabpanel-${index}`,
  };
}

function ProjectSettings() {
  const { t } = useTranslation("settings");
  const { currentUserRole } = useProject();
  const isOwner = currentUserRole === "owner";
  const previewMode = usePreviewMode();
  const [project, setProject] = useState({
    name: "",
    description: "",
    members_can_see_other_members: true,
    icon: null,
  });
  const [previewImage, setPreviewImage] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [confirmDeleteText, setConfirmDeleteText] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [tabValue, setTabValue] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();

  // Portal Display state
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [pushDialogOpen, setPushDialogOpen] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState({
    name: "",
    description: "",
    is_seasonal: false,
    season_label: "",
    date_range_config: {
      mode: "offset",
      months_back: 3,
      window_months: 1,
      period_start: "",
      period_end: "",
    },
    dashboard_config: buildDefaultConfig(DASHBOARD_CARDS),
    analytics_config: buildDefaultConfig(ANALYTICS_CARDS),
    asset_analytics_config: buildDefaultConfig(ASSET_ANALYTICS_CARDS),
    colorway_config: { ...DEFAULT_COLORWAY },
  });
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [templateTabValue, setTemplateTabValue] = useState(0);
  const [savingPreferences, setSavingPreferences] = useState(false);
  const [resettingPreferences, setResettingPreferences] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareTemplateId, setShareTemplateId] = useState(null);
  const [shareTargetProjectId, setShareTargetProjectId] = useState("");
  const [myProjects, setMyProjects] = useState([]);
  const [sharing, setSharing] = useState(false);

  useEffect(() => {
    // Check for tab parameter in URL
    const searchParams = new URLSearchParams(location.search);
    const tabParam = searchParams.get("tab");
    if (tabParam) {
      setTabValue(parseInt(tabParam, 10));
    }
  }, [location.search]);

  useEffect(() => {
    fetchProjectInfo();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchProjectInfo = async () => {
    try {
      setLoading(true);
      const projectData = await getProjectInfo();
      setProject({
        name: projectData.name || "",
        description: projectData.description || "",
        members_can_see_other_members:
          projectData.members_can_see_other_members ?? true,
        icon: projectData.icon || null,
        users: projectData.users || [],
      });
    } catch (error) {
      setError(t("projectSettings.messages.loadFailed"));
      console.error("Error fetching project info:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setProject((prev) => ({
      ...prev,
      [field]: value,
    }));
    // Clear messages when user starts typing
    if (error) setError("");
    if (success) setSuccess("");
  };

  const handleImageChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImage(reader.result);
      };
      reader.readAsDataURL(file);
      // Clear messages
      if (error) setError("");
      if (success) setSuccess("");
    }
  };

  const handleSave = async () => {
    if (!project.name.trim()) {
      setError(t("projectSettings.messages.nameRequired"));
      return;
    }

    try {
      setSaving(true);
      setError("");
      setSuccess("");

      const formData = new FormData();
      formData.append("name", project.name.trim());
      formData.append("description", project.description.trim());
      formData.append(
        "members_can_see_other_members",
        project.members_can_see_other_members,
      );

      if (imageFile) {
        formData.append("icon", imageFile);
      }

      const response = await updateProjectInfo(formData);

      if (response.error) {
        setError(response.error);
      } else {
        setSuccess(t("projectSettings.messages.saveSuccess"));
        setImageFile(null);
        setPreviewImage(null);
        // Refresh project info to get updated icon URL
        await fetchProjectInfo();
      }
    } catch (error) {
      setError(t("projectSettings.messages.saveFailed"));
      console.error("Error updating project:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteProject = async () => {
    if (confirmDeleteText !== project.name) {
      setError(t("projectSettings.messages.nameMismatch"));
      return;
    }

    try {
      setDeleting(true);
      setError("");

      const response = await deleteProject();

      if (response.error) {
        setError(response.error);
      } else {
        // Redirect to projects list after successful deletion
        navigate("/my-projects");
        window.location.reload(); // Refresh to update the project context
      }
    } catch (error) {
      setError(t("projectSettings.messages.deleteFailed"));
      console.error("Error deleting project:", error);
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
      setConfirmDeleteText("");
    }
  };

  const openDeleteDialog = () => {
    setDeleteDialogOpen(true);
    setConfirmDeleteText("");
    setError("");
  };

  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setConfirmDeleteText("");
    setError("");
  };

  // Portal Display handlers
  const fetchTemplates = async () => {
    try {
      setTemplatesLoading(true);
      const data = await getDisplayTemplates();
      setTemplates(data);
    } catch (err) {
      console.error("Error fetching templates:", err);
    } finally {
      setTemplatesLoading(false);
    }
  };

  const openNewTemplateDialog = () => {
    setEditingTemplate(null);
    setTemplateForm({
      name: "",
      description: "",
      is_seasonal: false,
      season_label: "",
      date_range_config: {
        mode: "offset",
        months_back: 3,
        window_months: 1,
        period_start: "",
        period_end: "",
      },
      dashboard_config: buildDefaultConfig(DASHBOARD_CARDS),
      analytics_config: buildDefaultConfig(ANALYTICS_CARDS),
      asset_analytics_config: buildDefaultConfig(ASSET_ANALYTICS_CARDS),
      colorway_config: { ...DEFAULT_COLORWAY },
    });
    setTemplateTabValue(0);
    setTemplateDialogOpen(true);
  };

  const openEditTemplateDialog = (template) => {
    setEditingTemplate(template);
    setTemplateForm({
      name: template.name,
      description: template.description || "",
      is_seasonal: template.is_seasonal,
      season_label: template.season_label || "",
      date_range_config: template.date_range_config || {
        mode: "offset",
        months_back: 3,
        window_months: 1,
        period_start: "",
        period_end: "",
      },
      dashboard_config:
        template.dashboard_config || buildDefaultConfig(DASHBOARD_CARDS),
      analytics_config:
        template.analytics_config || buildDefaultConfig(ANALYTICS_CARDS),
      asset_analytics_config:
        template.asset_analytics_config ||
        buildDefaultConfig(ASSET_ANALYTICS_CARDS),
      colorway_config: template.colorway_config || { ...DEFAULT_COLORWAY },
    });
    setTemplateTabValue(0);
    setTemplateDialogOpen(true);
  };

  // Card config update handler for SortableCardList
  const handleUpdateCardConfig = (configKey, updatedCards) => {
    setTemplateForm((prev) => ({ ...prev, [configKey]: updatedCards }));
  };

  const handleSaveTemplate = async () => {
    if (!templateForm.name.trim()) return;
    try {
      setSavingTemplate(true);
      if (editingTemplate) {
        await updateDisplayTemplate(editingTemplate.id, templateForm);
        toast.success(t("projectSettings.portalDisplay.templateUpdated"));
      } else {
        await createDisplayTemplate(templateForm);
        toast.success(t("projectSettings.portalDisplay.templateCreated"));
      }
      setTemplateDialogOpen(false);
      await fetchTemplates();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleActivateTemplate = async (templateId) => {
    try {
      await activateDisplayTemplate(templateId);
      toast.success(t("projectSettings.portalDisplay.templateActivated"));
      await fetchTemplates();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handlePushTemplate = async () => {
    try {
      setPushing(true);
      await pushDisplayTemplate();
      toast.success(t("projectSettings.portalDisplay.templatePushed"));
      setPushDialogOpen(false);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setPushing(false);
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    try {
      await deleteDisplayTemplate(templateId);
      toast.success(t("projectSettings.portalDisplay.templateDeleted"));
      await fetchTemplates();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const getDatePreview = (config) => {
    if (!config) return "—";
    const resolved = resolveDateRange(config);
    return `${resolved.period_start} to ${resolved.period_end}`;
  };

  // Producer preference handlers
  const handleSaveMyPreferences = async () => {
    try {
      setSavingPreferences(true);
      await saveMyPreferences({
        date_range_config: null, // Saves current localStorage settings as override
      });
      toast.success(t("projectSettings.myPreferences.saved"));
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSavingPreferences(false);
    }
  };

  const handleResetMyPreferences = async () => {
    try {
      setResettingPreferences(true);
      await resetMyPreferences();
      toast.success(t("projectSettings.myPreferences.reset"));
    } catch (err) {
      toast.error(err.message);
    } finally {
      setResettingPreferences(false);
    }
  };

  const openShareDialog = async (templateId) => {
    setShareTemplateId(templateId);
    setShareTargetProjectId("");
    try {
      const projects = await getMyProjects();
      // Filter out current project
      setMyProjects(
        projects.filter((p) => p.id !== project.id && p.id !== undefined),
      );
    } catch {
      setMyProjects([]);
    }
    setShareDialogOpen(true);
  };

  const handleShareTemplate = async () => {
    if (!shareTemplateId || !shareTargetProjectId) return;
    try {
      setSharing(true);
      await shareDisplayTemplate(shareTemplateId, shareTargetProjectId);
      toast.success(t("projectSettings.portalDisplay.templateShared"));
      setShareDialogOpen(false);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSharing(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    // Fetch templates when Portal Display tab is selected
    if (isOwner && newValue === 3) {
      fetchTemplates();
    }
  };

  if (loading) {
    return (
      <Container sx={{ pt: 3 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "400px",
          }}
        >
          <Spinner animation="border" />
        </Box>
      </Container>
    );
  }

  return (
    <Box>
      <PageHeader
        title={t("projectSettings.title")}
        description={t("projectSettings.description")}
      />

      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="project settings tabs"
        >
          <Tab label={t("projectSettings.tabs.general")} {...a11yProps(0)} />
          <Tab
            label={t("projectSettings.tabs.viewManagement")}
            {...a11yProps(1)}
          />
          <Tab label={t("projectSettings.tabs.dangerZone")} {...a11yProps(2)} />
          {isOwner && (
            <Tab
              label={t("projectSettings.tabs.portalDisplay")}
              {...a11yProps(3)}
            />
          )}
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            {success && (
              <Alert severity="success" sx={{ mb: 3 }}>
                {success}
              </Alert>
            )}

            <ProjectIconUpload
              icon={project.icon}
              previewImage={previewImage}
              handleImageChange={handleImageChange}
            />

            <Box sx={{ pt: 3, mb: 3 }}>
              <TextField
                fullWidth
                label={t("projectSettings.general.projectName")}
                value={project.name}
                onChange={(e) => handleInputChange("name", e.target.value)}
                variant="outlined"
                required
                helperText={t("projectSettings.general.projectNameHelper")}
              />
            </Box>

            <Box sx={{ mb: 4 }}>
              <TextField
                fullWidth
                label={t("projectSettings.general.description")}
                value={project.description}
                onChange={(e) =>
                  handleInputChange("description", e.target.value)
                }
                variant="outlined"
                multiline
                rows={4}
                helperText={t("projectSettings.general.descriptionHelper")}
              />
            </Box>

            <Button
              variant="contained"
              color="primary"
              onClick={handleSave}
              disabled={saving}
            >
              {saving
                ? t("projectSettings.general.saving")
                : t("projectSettings.general.saveChanges")}
            </Button>
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <Grid container spacing={3}>
          <Grid xs={12} lg={8}>
            <Card>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h6" sx={{ mb: 3 }} gutterBottom>
                  {t("projectSettings.viewManagement.title")}
                </Typography>

                {error && (
                  <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                  </Alert>
                )}

                {success && (
                  <Alert severity="success" sx={{ mb: 3 }}>
                    {success}
                  </Alert>
                )}

                <Box sx={{ mb: 4 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={project.members_can_see_other_members}
                        onChange={(e) =>
                          handleInputChange(
                            "members_can_see_other_members",
                            e.target.checked,
                          )
                        }
                        color="primary"
                      />
                    }
                    label={t(
                      "projectSettings.viewManagement.allowMembersToSee",
                    )}
                  />
                  <Typography
                    variant="bodySm"
                    component="p"
                    color="text.secondary"
                    sx={{ mt: 1, ml: 6 }}
                  >
                    {t(
                      "projectSettings.viewManagement.allowMembersToSeeDescription",
                    )}
                  </Typography>
                </Box>

                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving
                    ? t("projectSettings.general.saving")
                    : t("projectSettings.general.saveChanges")}
                </Button>
              </CardContent>
            </Card>

            {!isOwner && (
              <Card sx={{ mt: 3 }}>
                <CardContent sx={{ p: 4 }}>
                  <Typography variant="h6" sx={{ mb: 1 }} gutterBottom>
                    {t("projectSettings.myPreferences.title")}
                  </Typography>
                  <Typography
                    variant="bodySm"
                    color="text.secondary"
                    component="p"
                    sx={{ mb: 3 }}
                  >
                    {t("projectSettings.myPreferences.description")}
                  </Typography>
                  <Box sx={{ display: "flex", gap: 2 }}>
                    <Button
                      variant="contained"
                      onClick={handleSaveMyPreferences}
                      disabled={savingPreferences}
                    >
                      {savingPreferences
                        ? t("projectSettings.general.saving")
                        : t("projectSettings.myPreferences.saveButton")}
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={handleResetMyPreferences}
                      disabled={resettingPreferences}
                    >
                      {resettingPreferences
                        ? t("projectSettings.myPreferences.resetting")
                        : t("projectSettings.myPreferences.resetButton")}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            )}
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <Grid container spacing={3}>
          <Grid xs={12} lg={8}>
            <Card>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h6" gutterBottom color="error">
                  {t("projectSettings.dangerZone.title")}
                </Typography>

                <Divider sx={{ mb: 3 }} />

                <Box sx={{ mb: SPACING.sm }}>
                  <Typography variant="bodyMd" sx={{ mb: 3 }} gutterBottom>
                    {t("projectSettings.dangerZone.deleteProject")}
                  </Typography>
                  <br />
                  <Typography
                    variant="bodySm"
                    color="text.secondary"
                    sx={{ pb: 3 }}
                  >
                    {t("projectSettings.dangerZone.deleteProjectDescription")}
                  </Typography>
                  <br />
                  <br />
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={openDeleteDialog}
                    startIcon={<Trash2 size={18} />}
                  >
                    {t("projectSettings.dangerZone.deleteButton")}
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      {isOwner && (
        <TabPanel value={tabValue} index={3}>
          <Grid container spacing={3}>
            <Grid xs={12} lg={10}>
              <Card>
                <CardContent sx={{ p: 4 }}>
                  <Typography variant="h6" sx={{ mb: 1 }} gutterBottom>
                    {t("projectSettings.portalDisplay.title")}
                  </Typography>
                  <Typography
                    variant="bodySm"
                    color="text.secondary"
                    sx={{ mb: 3 }}
                    component="p"
                  >
                    {t("projectSettings.portalDisplay.description")}
                  </Typography>

                  {templatesLoading ? (
                    <Box
                      sx={{ display: "flex", justifyContent: "center", py: 4 }}
                    >
                      <Spinner animation="border" />
                    </Box>
                  ) : (
                    <>
                      {templates.length === 0 ? (
                        <Alert severity="info" sx={{ mb: 3 }}>
                          {t("projectSettings.portalDisplay.noTemplates")}
                        </Alert>
                      ) : (
                        <Box sx={{ mb: 3 }}>
                          {templates.map((template) => (
                            <Box
                              key={template.id}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                p: 2,
                                mb: 1,
                                border: "1px solid",
                                borderColor: template.is_default
                                  ? "primary.main"
                                  : "divider",
                                borderRadius: 1,
                                backgroundColor: template.is_default
                                  ? "action.selected"
                                  : "transparent",
                              }}
                            >
                              <Box>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    mb: 0.5,
                                  }}
                                >
                                  <Typography variant="bodyMd" fontWeight={600}>
                                    {template.name}
                                  </Typography>
                                  {template.is_default && (
                                    <Chip
                                      label={t(
                                        "projectSettings.portalDisplay.active",
                                      )}
                                      size="small"
                                      color="primary"
                                    />
                                  )}
                                  {template.is_seasonal &&
                                    template.season_label && (
                                      <Chip
                                        label={template.season_label}
                                        size="small"
                                        variant="outlined"
                                      />
                                    )}
                                </Box>
                                <Typography
                                  variant="bodySm"
                                  color="text.secondary"
                                >
                                  {t("projectSettings.portalDisplay.dateRange")}
                                  : {getDatePreview(template.date_range_config)}
                                </Typography>
                              </Box>
                              <Box sx={{ display: "flex", gap: 1 }}>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={() =>
                                    openEditTemplateDialog(template)
                                  }
                                >
                                  {t("common:actions.edit")}
                                </Button>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  startIcon={<Eye size={14} />}
                                  onClick={() => {
                                    previewMode.enterPreviewMode(template);
                                    navigate("/");
                                  }}
                                >
                                  {t(
                                    "projectSettings.portalDisplay.previewTemplate",
                                  )}
                                </Button>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={() => openShareDialog(template.id)}
                                >
                                  {t("projectSettings.portalDisplay.share")}
                                </Button>
                                {!template.is_default && (
                                  <Button
                                    variant="contained"
                                    size="small"
                                    onClick={() =>
                                      handleActivateTemplate(template.id)
                                    }
                                  >
                                    {t(
                                      "projectSettings.portalDisplay.activate",
                                    )}
                                  </Button>
                                )}
                                {template.is_default && (
                                  <Button
                                    variant="contained"
                                    size="small"
                                    onClick={() => setPushDialogOpen(true)}
                                  >
                                    {t(
                                      "projectSettings.portalDisplay.pushToAll",
                                    )}
                                  </Button>
                                )}
                                {!template.is_default && (
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    color="error"
                                    onClick={() =>
                                      handleDeleteTemplate(template.id)
                                    }
                                  >
                                    {t("common:actions.delete")}
                                  </Button>
                                )}
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      )}

                      <Button
                        variant="contained"
                        onClick={openNewTemplateDialog}
                      >
                        {t("projectSettings.portalDisplay.createTemplate")}
                      </Button>
                    </>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>
      )}

      {/* Template Create/Edit Dialog */}
      <Dialog
        open={templateDialogOpen}
        onClose={() => setTemplateDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingTemplate
            ? t("projectSettings.portalDisplay.editTemplate")
            : t("projectSettings.portalDisplay.createTemplate")}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: "flex", flexDirection: "column", gap: 2 }}>
            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField
                label={t("projectSettings.portalDisplay.templateName")}
                value={templateForm.name}
                onChange={(e) =>
                  setTemplateForm((prev) => ({ ...prev, name: e.target.value }))
                }
                required
                sx={{ flex: 2 }}
              />
              <TextField
                label={t("projectSettings.portalDisplay.templateDescription")}
                value={templateForm.description}
                onChange={(e) =>
                  setTemplateForm((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
                sx={{ flex: 3 }}
              />
            </Box>

            <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
              <Tabs
                value={templateTabValue}
                onChange={(e, v) => setTemplateTabValue(v)}
                variant="scrollable"
                scrollButtons="auto"
              >
                <Tab label={t("projectSettings.portalDisplay.dateRangeMode")} />
                <Tab label={t("projectSettings.portalDisplay.tabDashboard")} />
                <Tab label={t("projectSettings.portalDisplay.tabAnalytics")} />
                <Tab
                  label={t("projectSettings.portalDisplay.tabAssetAnalytics")}
                />
                <Tab label={t("projectSettings.portalDisplay.tabColorway")} />
              </Tabs>
            </Box>

            {/* Date Range Tab */}
            {templateTabValue === 0 && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <RadioGroup
                  value={templateForm.date_range_config.mode}
                  onChange={(e) =>
                    setTemplateForm((prev) => ({
                      ...prev,
                      date_range_config: {
                        ...prev.date_range_config,
                        mode: e.target.value,
                      },
                    }))
                  }
                >
                  <FormControlLabel
                    value="offset"
                    control={<Radio />}
                    label={t("projectSettings.portalDisplay.offsetMode")}
                  />
                  <FormControlLabel
                    value="explicit"
                    control={<Radio />}
                    label={t("projectSettings.portalDisplay.explicitMode")}
                  />
                </RadioGroup>

                {templateForm.date_range_config.mode === "offset" ? (
                  <Box sx={{ display: "flex", gap: 2 }}>
                    <TextField
                      type="number"
                      label={t("projectSettings.portalDisplay.monthsBack")}
                      value={templateForm.date_range_config.months_back}
                      onChange={(e) =>
                        setTemplateForm((prev) => ({
                          ...prev,
                          date_range_config: {
                            ...prev.date_range_config,
                            months_back: parseInt(e.target.value, 10) || 0,
                          },
                        }))
                      }
                      inputProps={{ min: 0, max: 60 }}
                      sx={{ flex: 1 }}
                    />
                    <TextField
                      type="number"
                      label={t("projectSettings.portalDisplay.windowMonths")}
                      value={templateForm.date_range_config.window_months}
                      onChange={(e) =>
                        setTemplateForm((prev) => ({
                          ...prev,
                          date_range_config: {
                            ...prev.date_range_config,
                            window_months: parseInt(e.target.value, 10) || 1,
                          },
                        }))
                      }
                      inputProps={{ min: 1, max: 24 }}
                      sx={{ flex: 1 }}
                    />
                  </Box>
                ) : (
                  <Box sx={{ display: "flex", gap: 2 }}>
                    <TextField
                      type="date"
                      label={t("projectSettings.portalDisplay.periodStart")}
                      value={templateForm.date_range_config.period_start}
                      onChange={(e) =>
                        setTemplateForm((prev) => ({
                          ...prev,
                          date_range_config: {
                            ...prev.date_range_config,
                            period_start: e.target.value,
                          },
                        }))
                      }
                      InputLabelProps={{ shrink: true }}
                      sx={{ flex: 1 }}
                    />
                    <TextField
                      type="date"
                      label={t("projectSettings.portalDisplay.periodEnd")}
                      value={templateForm.date_range_config.period_end}
                      onChange={(e) =>
                        setTemplateForm((prev) => ({
                          ...prev,
                          date_range_config: {
                            ...prev.date_range_config,
                            period_end: e.target.value,
                          },
                        }))
                      }
                      InputLabelProps={{ shrink: true }}
                      sx={{ flex: 1 }}
                    />
                  </Box>
                )}

                <Alert severity="info">
                  {t("projectSettings.portalDisplay.preview")}:{" "}
                  <strong>
                    {getDatePreview(templateForm.date_range_config)}
                  </strong>
                </Alert>

                <Divider />

                <FormControlLabel
                  control={
                    <Switch
                      checked={templateForm.is_seasonal}
                      onChange={(e) =>
                        setTemplateForm((prev) => ({
                          ...prev,
                          is_seasonal: e.target.checked,
                        }))
                      }
                    />
                  }
                  label={t("projectSettings.portalDisplay.seasonalTemplate")}
                />
                {templateForm.is_seasonal && (
                  <TextField
                    fullWidth
                    label={t("projectSettings.portalDisplay.seasonLabel")}
                    value={templateForm.season_label}
                    onChange={(e) =>
                      setTemplateForm((prev) => ({
                        ...prev,
                        season_label: e.target.value,
                      }))
                    }
                    placeholder="e.g., Holiday 2026, Q1 Review"
                  />
                )}
              </Box>
            )}

            {/* Card Config Tabs (Dashboard, Analytics, Asset Analytics) */}
            {templateTabValue === 1 && (
              <SortableCardList
                cards={templateForm.dashboard_config}
                registry={DASHBOARD_CARDS}
                configKey="dashboard_config"
                onUpdate={handleUpdateCardConfig}
              />
            )}
            {templateTabValue === 2 && (
              <SortableCardList
                cards={templateForm.analytics_config}
                registry={ANALYTICS_CARDS}
                configKey="analytics_config"
                onUpdate={handleUpdateCardConfig}
              />
            )}
            {templateTabValue === 3 && (
              <SortableCardList
                cards={templateForm.asset_analytics_config}
                registry={ASSET_ANALYTICS_CARDS}
                configKey="asset_analytics_config"
                onUpdate={handleUpdateCardConfig}
              />
            )}

            {/* Colorway Tab */}
            {templateTabValue === 4 && (
              <Box>
                <Typography
                  variant="bodySm"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  {t("projectSettings.portalDisplay.colorwayDescription")}
                </Typography>

                {/* Preset buttons */}
                <Box sx={{ display: "flex", gap: 1, mb: 3, flexWrap: "wrap" }}>
                  {COLORWAY_PRESETS.map((preset) => (
                    <Button
                      key={preset.key}
                      variant={
                        JSON.stringify(templateForm.colorway_config) ===
                        JSON.stringify(preset.colors)
                          ? "contained"
                          : "outlined"
                      }
                      size="small"
                      onClick={() =>
                        setTemplateForm((prev) => ({
                          ...prev,
                          colorway_config: { ...preset.colors },
                        }))
                      }
                    >
                      {t(preset.label)}
                    </Button>
                  ))}
                </Box>

                <Divider sx={{ mb: 2 }} />

                {/* Individual color slots */}
                {Object.entries(COLOR_SLOT_LABELS).map(([slot, labelKey]) => (
                  <Box
                    key={slot}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      py: 1,
                      borderBottom: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    <Typography variant="bodySm" sx={{ flex: 1 }}>
                      {t(labelKey)}
                    </Typography>
                    <Box
                      component="input"
                      type="color"
                      value={templateForm.colorway_config?.[slot] || "#009efd"}
                      onChange={(e) =>
                        setTemplateForm((prev) => ({
                          ...prev,
                          colorway_config: {
                            ...prev.colorway_config,
                            [slot]: e.target.value,
                          },
                        }))
                      }
                      sx={{
                        width: 36,
                        height: 36,
                        border: "2px solid",
                        borderColor: "divider",
                        borderRadius: 1,
                        cursor: "pointer",
                        padding: 0,
                        backgroundColor: "transparent",
                      }}
                    />
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            variant="secondary"
            onClick={() => setTemplateDialogOpen(false)}
            disabled={savingTemplate}
          >
            {t("common:actions.cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveTemplate}
            disabled={savingTemplate || !templateForm.name.trim()}
          >
            {savingTemplate
              ? t("projectSettings.general.saving")
              : editingTemplate
                ? t("projectSettings.portalDisplay.updateTemplate")
                : t("projectSettings.portalDisplay.createTemplate")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Push Confirmation Dialog */}
      <Dialog
        open={pushDialogOpen}
        onClose={() => setPushDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {t("projectSettings.portalDisplay.pushConfirmTitle")}
        </DialogTitle>
        <DialogContent>
          <Typography variant="bodyMd">
            {t("projectSettings.portalDisplay.pushConfirmDescription")}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            variant="secondary"
            onClick={() => setPushDialogOpen(false)}
            disabled={pushing}
          >
            {t("common:actions.cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={handlePushTemplate}
            disabled={pushing}
          >
            {pushing
              ? t("projectSettings.portalDisplay.pushing")
              : t("projectSettings.portalDisplay.pushToAll")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Share Template Dialog */}
      <Dialog
        open={shareDialogOpen}
        onClose={() => setShareDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {t("projectSettings.portalDisplay.shareTitle")}
        </DialogTitle>
        <DialogContent>
          <Typography variant="bodySm" color="text.secondary" sx={{ mb: 2 }}>
            {t("projectSettings.portalDisplay.shareDescription")}
          </Typography>
          {myProjects.length === 0 ? (
            <Alert severity="info">
              {t("projectSettings.portalDisplay.noOtherProjects")}
            </Alert>
          ) : (
            <TextField
              select
              fullWidth
              label={t("projectSettings.portalDisplay.targetProject")}
              value={shareTargetProjectId}
              onChange={(e) => setShareTargetProjectId(e.target.value)}
            >
              {myProjects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </TextField>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            variant="secondary"
            onClick={() => setShareDialogOpen(false)}
            disabled={sharing}
          >
            {t("common:actions.cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={handleShareTemplate}
            disabled={sharing || !shareTargetProjectId}
          >
            {sharing
              ? t("projectSettings.portalDisplay.sharing")
              : t("projectSettings.portalDisplay.share")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteDialogOpen}
        onClose={closeDeleteDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {t("projectSettings.dangerZone.deleteModal.title")}
        </DialogTitle>
        <DialogContent>
          <Typography variant="bodyMd" sx={{ mb: SPACING.sm }}>
            {t("projectSettings.dangerZone.deleteModal.description")}
            <strong> "{project.name}" </strong>
            and all of its data.
          </Typography>
          <br /> <br />
          <Typography variant="bodySm">
            {t("projectSettings.dangerZone.deleteModal.confirmText")}{" "}
            <strong>{project.name}</strong>{" "}
            {t("projectSettings.dangerZone.deleteModal.toConfirm")}
          </Typography>
          <br />
          <TextField
            fullWidth
            value={confirmDeleteText}
            onChange={(e) => setConfirmDeleteText(e.target.value)}
            placeholder={project.name}
            variant="outlined"
            error={
              confirmDeleteText !== "" && confirmDeleteText !== project.name
            }
          />
          {error && (
            <Alert severity="error" sx={{ mt: SPACING.sm }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            variant="secondary"
            onClick={closeDeleteDialog}
            disabled={deleting}
          >
            {t("common:actions.cancel")}
          </Button>
          <Button
            onClick={handleDeleteProject}
            color="error"
            variant="contained"
            disabled={deleting || confirmDeleteText !== project.name}
          >
            {deleting
              ? t("projectSettings.dangerZone.deleteModal.deleting")
              : t("projectSettings.dangerZone.deleteModal.deleteButton")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ProjectSettings;
